if (!customElements.get('product-sticky-atc-bar')) {
  customElements.define(
    'product-sticky-atc-bar',
    class ProductStickyAtcBar extends HTMLElement {
      constructor() {
        super();
        this.syncing = false;
        this.mobileMql = window.matchMedia('(max-width: 749px)');
      }

      connectedCallback() {
        this.sectionId = this.dataset.section;
        this.layout = this.dataset.layout;
        this.sentinel = document.getElementById(`ProductBuyButtonsSentinel-${this.sectionId}`);
        this.mainVariantSelects = document.getElementById(`variant-selects-${this.sectionId}`);
        this.stickyVariantRoots = this.querySelectorAll('[data-sticky-variant-selects]');
        this.mainSubmit = document.getElementById(`ProductSubmitButton-${this.sectionId}`);
        this.mainForm = document.getElementById(`product-form-${this.sectionId}`);

        this.bindStickyVariants();
        this.bindSubmitSync();
        this.syncFromMain();
        this.syncSubmitButton();

        if (this.sentinel && this.layout !== 'mobile') {
          this.observeSentinel();
          this.mobileMql.addEventListener('change', () => this.handleBreakpoint());
          this.handleBreakpoint();
        }

        this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          if (event.data.sectionId !== this.sectionId) return;
          this.syncFromMain();
          this.syncSubmitButton();
        });

        this.cartErrorUnsubscriber = subscribe(PUB_SUB_EVENTS.cartError, () => {
          this.resetStickySubmit();
        });

        this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          if (event.source !== 'product-form') return;
          this.resetStickySubmit();
        });

        this.observeMainSubmitLoading();
      }

      disconnectedCallback() {
        if (this.observer) this.observer.disconnect();
        if (this.variantChangeUnsubscriber) this.variantChangeUnsubscriber();
        if (this.cartErrorUnsubscriber) this.cartErrorUnsubscriber();
        if (this.cartUpdateUnsubscriber) this.cartUpdateUnsubscriber();
        if (this.submitObserver) this.submitObserver.disconnect();
      }

      getStickySubmitButtons() {
        return document.querySelectorAll(
          `#ProductSubmitButton-Sticky-${this.sectionId}, #ProductSubmitButton-Sticky-${this.sectionId}-mobile`
        );
      }

      handleBreakpoint() {
        if (this.layout === 'mobile') return;
        if (this.mobileMql.matches) {
          this.classList.remove('is-visible');
          return;
        }
        if (this.sentinel) {
          const rect = this.sentinel.getBoundingClientRect();
          this.classList.toggle('is-visible', rect.bottom < 0);
        }
      }

      observeSentinel() {
        this.observer = new IntersectionObserver(
          ([entry]) => {
            if (this.mobileMql.matches) return;
            this.updateVisibility(entry);
          },
          { root: null, threshold: 0 }
        );
        this.observer.observe(this.sentinel);
      }

      updateVisibility(entry) {
        if (this.layout === 'mobile' || this.mobileMql.matches) return;
        const { boundingClientRect } = entry;
        const scrolledPastTop = boundingClientRect.bottom < 0;
        this.classList.toggle('is-visible', scrolledPastTop);
      }

      bindStickyVariants() {
        if (!this.mainVariantSelects) return;

        this.stickyVariantRoots.forEach((stickyRoot) => {
          stickyRoot.addEventListener('change', (event) => {
            if (this.syncing) return;
            const target = event.target;
            if (!target.matches('input[type="radio"], select')) return;
            this.syncToMain(target);
          });
        });

        this.mainVariantSelects.addEventListener('change', () => {
          if (this.syncing) return;
          this.syncFromMain();
        });
      }

      getOptionNameFromFieldName(name) {
        const match = name.match(/^options\[(.+)\]$/);
        return match ? match[1] : name;
      }

      syncToMain(target) {
        if (!this.mainVariantSelects) return;
        this.syncing = true;

        if (target.tagName === 'SELECT') {
          const mainSelect = this.mainVariantSelects.querySelector(`select[name="${CSS.escape(target.name)}"]`);
          if (mainSelect && mainSelect.value !== target.value) {
            mainSelect.value = target.value;
            mainSelect.dispatchEvent(new Event('change', { bubbles: true }));
          } else {
            const optionName = this.getOptionNameFromFieldName(target.name);
            const mainRadio = this.mainVariantSelects.querySelector(
              `input[type="radio"][name="${CSS.escape(optionName)}"][value="${CSS.escape(target.value)}"]`
            );
            if (mainRadio && !mainRadio.checked) {
              mainRadio.checked = true;
              mainRadio.dispatchEvent(new Event('change', { bubbles: true }));
            }
          }
        } else if (target.type === 'radio') {
          const mainRadio = this.mainVariantSelects.querySelector(
            `input[type="radio"][name="${CSS.escape(target.name)}"][value="${CSS.escape(target.value)}"]`
          );
          if (mainRadio && !mainRadio.checked) {
            mainRadio.checked = true;
            mainRadio.dispatchEvent(new Event('change', { bubbles: true }));
          }
        }

        this.syncing = false;
      }

      syncFromMain() {
        if (!this.mainVariantSelects) return;

        this.syncing = true;
        this.stickyVariantRoots.forEach((stickyRoot) => {
          this.syncStickyRootFromMain(stickyRoot);
        });
        this.syncing = false;
      }

      syncStickyRootFromMain(stickyRoot) {
        this.mainVariantSelects.querySelectorAll('select').forEach((mainSelect) => {
          const stickySelect = stickyRoot.querySelector(`select[name="${CSS.escape(mainSelect.name)}"]`);
          if (stickySelect) stickySelect.value = mainSelect.value;
        });

        this.mainVariantSelects.querySelectorAll('fieldset').forEach((fieldset) => {
          const checked = fieldset.querySelector('input[type="radio"]:checked');
          if (!checked) return;

          const optionName = checked.name;
          const optionsSelectName = `options[${optionName}]`;
          const stickySelect = stickyRoot.querySelector(`select[name="${CSS.escape(optionsSelectName)}"]`);

          if (stickySelect) {
            stickySelect.value = checked.value;
            return;
          }

          const stickyFieldsets = stickyRoot.querySelectorAll('fieldset');
          stickyFieldsets.forEach((stickyFieldset) => {
            stickyFieldset.querySelectorAll('input[type="radio"]').forEach((radio) => {
              radio.checked = false;
            });
            const stickyRadio = stickyFieldset.querySelector(
              `input[type="radio"][value="${CSS.escape(checked.value)}"]`
            );
            if (stickyRadio) stickyRadio.checked = true;
          });
        });
      }

      bindSubmitSync() {
        if (!this.mainForm) return;
        if (this.mainForm.dataset.stickyAtcSubmitBound) return;
        this.mainForm.dataset.stickyAtcSubmitBound = 'true';

        this.mainForm.addEventListener('submit', () => {
          this.getStickySubmitButtons().forEach((button) => {
            button.setAttribute('aria-disabled', 'true');
            button.classList.add('loading');
            button.querySelector('.loading__spinner')?.classList.remove('hidden');
          });
        });
      }

      observeMainSubmitLoading() {
        if (!this.mainSubmit) return;

        this.submitObserver = new MutationObserver(() => {
          if (!this.mainSubmit.classList.contains('loading')) {
            this.resetStickySubmit();
          }
        });

        this.submitObserver.observe(this.mainSubmit, {
          attributes: true,
          attributeFilter: ['class', 'aria-disabled'],
        });
      }

      resetStickySubmit() {
        const mainLabel = this.mainSubmit?.querySelector('span');

        this.getStickySubmitButtons().forEach((button) => {
          button.classList.remove('loading');
          button.removeAttribute('aria-disabled');
          button.querySelector('.loading__spinner')?.classList.add('hidden');

          if (this.mainSubmit?.hasAttribute('disabled')) {
            button.setAttribute('disabled', 'disabled');
          } else {
            button.removeAttribute('disabled');
          }

          const stickyLabel = button.querySelector('span');
          if (!stickyLabel) return;

          if (this.mainSubmit?.hasAttribute('disabled')) {
            stickyLabel.textContent = mainLabel?.textContent || window.variantStrings.soldOut;
          } else {
            stickyLabel.textContent = window.variantStrings.addToCartShort || window.variantStrings.addToCart;
          }
        });
      }

      syncSubmitButton() {
        this.resetStickySubmit();
      }
    }
  );
}
